import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import PagedTable from '../../components/sorted-table/PagedTable';
import { Loader } from '../../components';
import { SearchSVG } from '../../components/svgs';
import DateRangeInput from '../../components/custom-input/DateInputRange';
import { MarketPlacePaymentTransactionOrderDetail } from '../../types/interfaces/marketPlace.interfaces';
import { StandardParamsToODataParams } from '../../types/interfaces/apiParams.interfaces';
import { getMarketPlaceMyOrders } from '../../api/marketPlacePaymentTransactionOrderDetailApi';
import { toastMessages } from '../../constants/errorMessages';
import {
  LABEL_CONSTANTS,
  MARKETPLACE_LISTING_CONSTANTS,
  MARKETPLACE_MY_ORDERS_LABELS,
  NAVIGATION_LINK_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../constants/common';
import { centsToDollars } from '../../services/account.service';
import { showToast } from '../../services/toast.service';
import { dayAfterDate, formatDate } from '../../utils/dateHandler';
import './myOrders.scss';

const ordersFetchLimit = 10;

const MyOrders = (): JSX.Element => {
  const activeAccountId = useAppSelector(state => state.activeAccount.accountId);
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [productSearch, setProductSearch] = useState('');
  const [filter, setFilter] = useState<string | undefined>(undefined);

  const [rows, setRows] = useState<MarketPlacePaymentTransactionOrderDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevFilterRef = useRef<string | undefined>(undefined);
  const isLoadingAdditionalPage: MutableRefObject<boolean> = useRef(false);
  const hasAllResults: MutableRefObject<boolean> = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const callNumber: MutableRefObject<number> = useRef(0);

  const buildFilterString = useCallback(
    (range: { startDate: Date | null; endDate: Date | null }, product: string): string | undefined => {
      const conditions: string[] = [];

      if (range.startDate && range.endDate) {
        const start = new Date(range.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(range.endDate);
        end.setHours(0, 0, 0, 0);
        conditions.push(
          `(createdDateTime ge ${start.toISOString()}) and (createdDateTime lt ${dayAfterDate(end).toISOString()})`,
        );
      }

      if (product.trim()) {
        const filteredProduct = product.toLowerCase().trim().replace(/'/g, "''");
        conditions.push(`contains(tolower(inventoryTransaction/specimen/animal/name), '${filteredProduct}')`);
      }

      return conditions.length > 0 ? conditions.join(' and ') : undefined;
    },
    [],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilter(buildFilterString(dateRange, productSearch));
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [dateRange, productSearch, buildFilterString]);

  useEffect(() => {
    if (filter === prevFilterRef.current) return;
    prevFilterRef.current = filter;
    hasAllResults.current = false;
    setRows([]);
    setOffset(0);
  }, [filter]);

  const getData = useCallback(
    (params: any) => {
      const odata = StandardParamsToODataParams({ ...params });
      return getMarketPlaceMyOrders({
        ...odata,
        accountId: activeAccountId,
      });
    },
    [activeAccountId],
  );

  const buildRow = useCallback(
    (order: MarketPlacePaymentTransactionOrderDetail) => [
      order.inventoryTransactionId,
      <div key={`product-${order.inventoryTransactionId}`}>
        <div className="product-name">
          {order.animal?.specimens?.[0]?.specimenId} - {order.animal?.name}
        </div>
        <div className="product-sub">{`${order.breed?.name} - ${order?.specimenType?.name}`}</div>
      </div>,
      order.sellerAccount?.name,
      order.quantity,
      <span key={`total-${order.inventoryTransactionId}`} className="total-amount">
        ${centsToDollars(order.sellerAmountCents + order.rsgCommissionCents)}
      </span>,
      formatDate(order.createdDateTime),
    ],
    [],
  );

  const loadData = useCallback(
    async (pageOffset: number) => {
      if (hasAllResults.current) return;
      callNumber.current++;
      const callNum = callNumber.current;

      try {
        setLoading(true);

        const odata = StandardParamsToODataParams({
          filter: prevFilterRef.current,
          sort: '^createdDateTime',
          limit: ordersFetchLimit,
          offset: pageOffset,
        });

        const { data: response } = await getMarketPlaceMyOrders({
          ...odata,
          accountId: activeAccountId,
        });

        if (callNum !== callNumber.current) return;

        const raw: MarketPlacePaymentTransactionOrderDetail[] = Array.isArray(response)
          ? response
          : (response as any)?.result ?? [];

        hasAllResults.current = raw.length < ordersFetchLimit;
        setRows(prev => (pageOffset === 0 ? raw : [...prev, ...raw]));
        setHasMore(raw.length === ordersFetchLimit);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoading(false);
        isLoadingAdditionalPage.current = false;
      }
    },
    [activeAccountId],
  );

  useEffect(() => {
    if (window.innerWidth <= 767) loadData(offset);
  }, [offset]);

  const handleScroll = useCallback(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    const atBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight <= 100;
    if (atBottom && !isLoadingAdditionalPage.current && !hasAllResults.current) {
      isLoadingAdditionalPage.current = true;
      setOffset(previous => previous + ordersFetchLimit);
    }
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    scrollContainer.addEventListener('scroll', handleScroll);
    scrollContainer.addEventListener('touchmove', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      scrollContainer.removeEventListener('touchmove', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    isLoadingAdditionalPage.current = false;
  }, [rows]);

  return (
    <div className="my-orders">
      <hr className="my-orders__header-hr" />
      <div className="my-orders__header">
        <h1 className="my-orders__title">{NAVIGATION_LINK_CONSTANTS.MY_ORDERS}</h1>
        <div className="my-orders__search-stack">
          <div className="my-orders__filter-group">
            <DateRangeInput value={dateRange} onChange={setDateRange} isClearable maxDate={new Date()} />
          </div>
          <div className="my-orders__filter-group my-orders__filter-group--search">
            <div className="my-orders__search-wrap">
              <input
                type="text"
                className="my-orders__search-input"
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                placeholder={MARKETPLACE_MY_ORDERS_LABELS.SEARCH_PRODUCTS}
              />
              <span className="my-orders__search-icon">
                <SearchSVG />
              </span>
            </div>
          </div>
        </div>
      </div>

      <hr className="my-orders__header-hr" />

      <div className="desk-my-orders">
        <PagedTable
          headers={[
            { displayName: TABLE_HEADER_CONSTANTS.ORDER_ID },
            { displayName: MARKETPLACE_MY_ORDERS_LABELS.PRODUCT },
            { displayName: MARKETPLACE_LISTING_CONSTANTS.SELLER },
            { displayName: MARKETPLACE_MY_ORDERS_LABELS.QTY },
            { displayName: MARKETPLACE_MY_ORDERS_LABELS.TOTAL },
            { displayName: TABLE_HEADER_CONSTANTS.DATE, sortValue: 'createdDateTime' },
          ]}
          getData={getData}
          buildRow={buildRow}
          sortBy="createdDateTime"
          sortDescByDefault
          filter={filter}
          height={500}
          pageSize={ordersFetchLimit}
          scrollBufferThickness={100}
          noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
        />
      </div>

      <div className="xs-my-orders" ref={scrollRef}>
        {loading && rows.length === 0 ? (
          <Loader addedSpace loaderSize="small" />
        ) : rows.length === 0 ? (
          <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
        ) : (
          <>
            {rows.map((order, index) => (
              <div className="card max-width" key={`order-${order.inventoryTransactionId}-${index}`}>
                <div className="order-management-card">
                  <div className="order-management-content">
                    <div className="order-info">
                      <div className="order-content">
                        <label>{TABLE_HEADER_CONSTANTS.ORDER_ID} : </label>
                        <label>{order.inventoryTransactionId}</label>
                      </div>
                      <div className="order-content">
                        <label>{MARKETPLACE_MY_ORDERS_LABELS.PRODUCT} : </label>
                        <label>
                          {order.animal?.specimens?.[0]?.specimenId} - {order.animal?.name ?? '-'}
                          <span className="product-sub">
                            {` (${order.animal?.breed?.name ?? '-'} - ${order.specimenType?.name ?? '-'})`}
                          </span>
                        </label>
                      </div>
                      <div className="order-content">
                        <label>{MARKETPLACE_LISTING_CONSTANTS.SELLER} : </label>
                        <label>{order.sellerAccount?.name ?? '-'}</label>
                      </div>
                    </div>
                    <div className="order-info">
                      <div className="order-content">
                        <label>{MARKETPLACE_MY_ORDERS_LABELS.QTY} : </label>
                        <label>{order.quantity}</label>
                      </div>
                      <div className="order-content">
                        <label>{MARKETPLACE_MY_ORDERS_LABELS.TOTAL} : </label>
                        <label className="total-amount">
                          ${centsToDollars(order.sellerAmountCents + order.rsgCommissionCents)}
                        </label>
                      </div>
                      <div className="order-content">
                        <label>{TABLE_HEADER_CONSTANTS.DATE} : </label>
                        <label>{formatDate(order.createdDateTime)}</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {hasMore && <Loader addedSpace loaderSize="small" />}
          </>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
